import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Container, 
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider, 
  Grid, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  TextField,
  Typography, 
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  ArrowBack, 
  Delete as DeleteIcon, 
  DeleteForever,
  Email as EmailIcon,
  InfoOutlined, 
  Lock as LockIcon,
  PersonOff as PersonOffIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Firebase imports
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getDatabase, ref as dbRef, get, push, set } from 'firebase/database';
import { useFirebaseService } from '../contexts/FirebaseServiceContext';

// Deletion status types
type DeletionStatus = 
  | { type: 'none' }
  | { type: 'data', verified: boolean }
  | { type: 'account', verified: boolean, scheduledDate: Date };

// Calculate deletion date - 15 days from now
const getDeletionDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 15);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Format date for display
const formatDate = (date: Date) => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long', 
    day: 'numeric'
  });
};

// Calculate days remaining until deletion
const getDaysRemaining = (date: Date) => {
  const today = new Date();
  const diffTime = date.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

const PrivacyPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [openDataDialog, setOpenDataDialog] = useState(false);
  const [openAccountDialog, setOpenAccountDialog] = useState(false);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [successDialog, setSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deletionStatus, setDeletionStatus] = useState<DeletionStatus>({ type: 'none' });
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [openImmediateDeleteDialog, setOpenImmediateDeleteDialog] = useState(false);
  const [immediateDeleteConfirm, setImmediateDeleteConfirm] = useState('');
  
  // Firebase functions
  const functions = getFunctions();
  const db = getDatabase();
  
  // Deletion date - 15 days from now
  const deletionDate = getDeletionDate();
  
  // Check for pending deletion requests on load
  useEffect(() => {
    const checkDeletionStatus = async () => {
      if (!currentUser) return;
      
      try {
        setCheckingStatus(true);
        // TODO: Migrate data fetching to Realtime Database
        // Placeholder: setDeletionStatus({ type: 'none' });
        setDeletionStatus({ type: 'none' });
      } catch (error) {
        console.error('Error checking deletion status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };
    
    checkDeletionStatus();
  }, [currentUser]);
  
  const handleDeleteData = async () => {
    setLoading(true);
    
    try {
      // Call the verifyDeletionRequest function directly with isAccountDeletion: false
      const deletionRequest = httpsCallable(functions, 'verifyDeletionRequest');
      const result = await deletionRequest({ isAccountDeletion: false });
      const data = result.data as any;
      
      setOpenDataDialog(false);
      
      // Check if the operation was successful
      if (data.success) {
        // Check if any data was found and deleted
        if (data.noDataFound) {
          setSuccessMessage('No data found to delete. Your account is already clean.');
        } else {
          setSuccessMessage('Your data has been successfully deleted.');
        }
        
        // Refresh the page after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        // Handle unsuccessful operation
        setSuccessMessage('Unable to process deletion request. Please try again later.');
      }
    } catch (error: any) {
      console.error('Error deleting data:', error);
      // Handle specific Firebase function errors
      const errorMessage = error.message || 'Error deleting data. Please try again later.';
      setSuccessMessage(errorMessage);
    } finally {
      setLoading(false);
      setSuccessDialog(true);
    }
  };
  
  const handleDeleteAccount = async () => {
    setLoading(true);
    
    try {
      const initiateDeletion = httpsCallable(functions, 'initiateDeletionRequest');
      const result = await initiateDeletion({ isAccountDeletion: true });
      const data = result.data as any;
      
      setOpenAccountDialog(false);
      setSuccessMessage(`We've sent a verification email to ${currentUser?.email}. Please follow the instructions to confirm account deletion. Your account will be permanently deleted on ${data.scheduledDeletionDate ? new Date(data.scheduledDeletionDate).toLocaleDateString() : deletionDate} if confirmed.`);
      setSuccessDialog(true);
      
      // Refresh the deletion status after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Error initiating account deletion:', error);
      setSuccessMessage('Error initiating account deletion. Please try again later.');
      setSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  };
  
  const handleImmediateDelete = async () => {
    setLoading(true);
    
    try {
      const executeImmediateDeletion = httpsCallable(functions, 'executeImmediateDeletion');
      await executeImmediateDeletion({});
      
      setOpenImmediateDeleteDialog(false);
      setSuccessMessage('Your account and all data have been permanently deleted. You will be signed out shortly.');
      setSuccessDialog(true);
      
      // Sign out and redirect after a short delay
      setTimeout(() => {
        // In a real app, this would sign the user out and redirect to login page
        window.location.href = '/';
      }, 3000);
    } catch (error) {
      console.error('Error executing immediate deletion:', error);
      setSuccessMessage('Error deleting account. Please try again later.');
      setSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  };
  
  const handleRestoreAccount = async () => {
    setLoading(true);
    
    try {
      const cancelDeletion = httpsCallable(functions, 'cancelDeletionRequest');
      await cancelDeletion({ userId: currentUser?.uid });
      
      setSuccessMessage('Your account has been restored successfully.');
      setSuccessDialog(true);
      
      // Refresh the deletion status
      setDeletionStatus({ type: 'none' });
    } catch (error) {
      console.error('Error restoring account:', error);
      setSuccessMessage('Error restoring account. Please try again later.');
      setSuccessDialog(true);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    if (!currentUser) {
      setSuccessMessage('You must be logged in to export your data.');
      setSuccessDialog(true);
      return;
    }

    setExporting(true);
    setOpenExportDialog(false);

    try {
      // Get user data from database
      const userRef = dbRef(db, `users/${currentUser.uid}`);
      const userSnapshot = await get(userRef);

      if (!userSnapshot.exists()) {
        throw new Error('No user data found.');
      }

      const userData = userSnapshot.val();
      
      // Get all grids for the user
      const gridsRef = dbRef(db, `users/${currentUser.uid}/grids`);
      const gridsSnapshot = await get(gridsRef);
      
      const grids = gridsSnapshot.exists() ? Object.values(gridsSnapshot.val()) : [];

      // Create export data object
      const exportData = {
        exportDate: new Date().toISOString(),
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userData: {
          email: userData.email,
          created_at: userData.created_at,
          subscription: userData.subscription,
          rewardCredits: userData.rewardCredits,
        },
        testData: {
          totalTests: grids.length,
          tests: grids
        }
      };

      // Convert to JSON string
      const jsonString = JSON.stringify(exportData, null, 2);
      
      // Create blob and download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `amsler_grid_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccessMessage(`Your data has been exported successfully. ${grids.length} test(s) included.`);
      setSuccessDialog(true);
    } catch (error) {
      console.error('Error exporting data:', error);
      setSuccessMessage('Error exporting data. Please try again later.');
      setSuccessDialog(true);
    } finally {
      setExporting(false);
    }
  };
  
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Button 
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          Privacy Policy & Data Management
        </Typography>
      </Box>
      
      {/* Account Deletion Warning Banner */}
      {deletionStatus.type === 'account' && deletionStatus.verified && (
        <Alert 
          severity="error"
          variant="filled"
          sx={{ mb: 3 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => handleRestoreAccount()}
              disabled={loading}
            >
              RESTORE
            </Button>
          }
        >
          Your account is scheduled for deletion in {getDaysRemaining(deletionStatus.scheduledDate)} days ({formatDate(deletionStatus.scheduledDate)})
        </Alert>
      )}
      
      {/* Account Deletion Card */}
      {deletionStatus.type === 'account' && deletionStatus.verified && (
        <Card elevation={3} sx={{ mb: 3, bgcolor: theme.palette.error.light }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <WarningIcon sx={{ color: theme.palette.error.dark, mr: 1 }} />
              <Typography variant="h5" component="h2" color="error.dark">
                Account Scheduled for Deletion
              </Typography>
            </Box>
            
            <Typography variant="body1" paragraph>
              Your account and all its data will be permanently deleted in <strong>{getDaysRemaining(deletionStatus.scheduledDate)} days</strong> (on {formatDate(deletionStatus.scheduledDate)}).
            </Typography>
            
            <Typography variant="body2" paragraph>
              You can continue to use the app during this period, but once the countdown ends, your account and all associated data will be permanently removed.
            </Typography>
            
            <Box sx={{ display: 'flex', mt: 2, gap: 2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => setOpenImmediateDeleteDialog(true)}
                disabled={loading}
                startIcon={<DeleteForever />}
              >
                Delete Now
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => handleRestoreAccount()}
                disabled={loading}
              >
                Restore Account
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Action Section */}
      <Card elevation={2} sx={{ mb: 3, bgcolor: theme.palette.primary.light + '20' }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <StorageIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            Manage Your Data
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={exporting ? <CircularProgress size={20} /> : <StorageIcon />}
              onClick={() => setOpenExportDialog(true)}
              disabled={exporting}
            >
              Download My Data
            </Button>
            
            <Button
              variant="contained"
              color="error"
              fullWidth
              startIcon={<DeleteIcon />}
              onClick={() => setOpenDataDialog(true)}
              disabled={loading || deletionStatus.type !== 'none'}
            >
              Request Deletion
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      <Grid container spacing={3}>
        {/* Data We Collect Section */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ color: '#6C63FF', mr: 1 }} />
                <Typography variant="h5" component="h2">
                  Data We Collect
                </Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                The Amsler Grid Daily app collects the following information:
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: '#6C63FF', 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Amsler Grid Test Results" 
                    secondary="Your marked grid positions and eye tested (left/right)"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: '#6C63FF', 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Test Images" 
                    secondary="Images of your completed tests saved to analyze progression"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: '#6C63FF', 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Account Information" 
                    secondary="Email address used for login and account management"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: '#6C63FF', 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Testing Date & Time" 
                    secondary="When you completed each test for tracking progression"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Privacy Rights Section */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ color: '#4ECDC4', mr: 1 }} />
                <Typography variant="h5" component="h2">
                  Your Privacy Rights
                </Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                Under various privacy regulations, you have rights regarding your personal data:
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: '#4ECDC4', 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Right to Access" 
                    secondary="You can request a copy of all data we store about you"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: '#4ECDC4', 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Right to Delete" 
                    secondary="You can request deletion of your data or entire account"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: '#4ECDC4', 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Right to Correction" 
                    secondary="You can request inaccurate data be corrected"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: '#4ECDC4', 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Right to Portability" 
                    secondary="You can download your data in a portable format"
                  />
                </ListItem>
              </List>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<EmailIcon />}
                component="a"
                href="mailto:privacy@kubomaster.llc?subject=Privacy%20Rights%20Request"
                sx={{ mt: 2 }}
              >
                Contact Privacy Team
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Data Deletion Section */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DeleteIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
                <Typography variant="h5" component="h2">
                  Delete Your Data
                </Typography>
              </Box>
              
              <Typography variant="body1" paragraph>
                You can delete your test data while keeping your account, or delete your entire account including all data.
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: theme.palette.warning.main, 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Delete Test Data" 
                    secondary="Immediately deletes all your test data while keeping your account active"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        bgcolor: theme.palette.error.main, 
                        borderRadius: 1 
                      }} 
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Delete Account" 
                    secondary={`Schedules your account for deletion with a 15-day retention period (until ${deletionDate})`}
                  />
                </ListItem>
              </List>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  startIcon={<DeleteIcon />}
                  onClick={() => setOpenDataDialog(true)}
                  disabled={loading || deletionStatus.type !== 'none'}
                >
                  Delete All My Test Data
                </Button>
                
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  startIcon={<DeleteForever />}
                  onClick={() => setOpenAccountDialog(true)}
                  disabled={loading || deletionStatus.type !== 'none'}
                >
                  Delete My Account & All Data
                </Button>
              </Box>
              
              {deletionStatus.type !== 'none' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  You have a pending deletion request. Please check your email for verification.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12}>
          <Typography variant="caption" align="center" sx={{ display: 'block' }}>
            Note: For major privacy requests, we'll send a verification email to confirm it's really you before processing your request.
          </Typography>
        </Grid>
      </Grid>
      
      {/* Dialogs */}
      
      {/* Delete Data Dialog */}
      <Dialog
        open={openDataDialog}
        onClose={() => !loading && setOpenDataDialog(false)}
      >
        <DialogTitle>Delete All Test Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all your test data? Your data will be permanently deleted immediately after verification. This action cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            <strong>Your account will remain active, but all test history will be removed.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDataDialog(false)} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteData} 
            color="error" 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            Delete All Data
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Account Dialog */}
      <Dialog
        open={openAccountDialog}
        onClose={() => !loading && setOpenAccountDialog(false)}
      >
        <DialogTitle>Delete Account & All Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete your account and all data? Your account will be scheduled for deletion and permanently removed on {deletionDate}. You'll receive an email confirmation, and you can cancel this request anytime before the permanent deletion date.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: theme.palette.error.main }}>
            <strong>This action cannot be undone after the retention period.</strong>
          </DialogContentText>
          
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Please type DELETE to confirm:
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type DELETE"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenAccountDialog(false)} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained"
            disabled={loading || confirmText !== 'DELETE'}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            Delete My Account
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Immediate Delete Dialog */}
      <Dialog
        open={openImmediateDeleteDialog}
        onClose={() => !loading && setOpenImmediateDeleteDialog(false)}
      >
        <DialogTitle>Delete Account Immediately</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you absolutely sure you want to delete your account and all data immediately? This action cannot be undone and will be executed right away.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: theme.palette.error.main }}>
            <strong>Your account and all data will be permanently deleted right now.</strong>
          </DialogContentText>
          
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Please type DELETE to confirm:
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            value={immediateDeleteConfirm}
            onChange={(e) => setImmediateDeleteConfirm(e.target.value)}
            placeholder="Type DELETE"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenImmediateDeleteDialog(false)} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImmediateDelete} 
            color="error" 
            variant="contained"
            disabled={loading || immediateDeleteConfirm !== 'DELETE'}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            Delete Now
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Export Dialog */}
      <Dialog
        open={openExportDialog}
        onClose={() => !exporting && setOpenExportDialog(false)}
      >
        <DialogTitle>Export Your Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will create a JSON file containing all your test data. The file will be downloaded to your device.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenExportDialog(false)} 
            disabled={exporting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExportData} 
            color="primary" 
            variant="contained"
            disabled={exporting}
            startIcon={exporting ? <CircularProgress size={20} /> : undefined}
          >
            Export
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={() => setSuccessDialog(false)}
      >
        <DialogTitle>Request Submitted</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {successMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PrivacyPage; 